import { useEffect, useState } from "react";
import { animate } from "framer-motion";

/**
 * Tween a numeric value from wherever it currently is to a new target
 * over a short duration. Returns the current in-flight value on each
 * frame so consumers can format it and render.
 *
 * Useful for money totals: when a new expense lands, the trip total
 * counts up instead of hard-swapping. Reduced-motion callers get the
 * final value immediately.
 *
 * Rounds to 2 decimals to match currency display; callers apply their
 * own `formatMoney(value)` on the returned number.
 */
export function useCountUp(target: number, opts: { duration?: number } = {}): number {
  const duration = opts.duration ?? 0.6;
  const [value, setValue] = useState(target);

  useEffect(() => {
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setValue(target);
      return;
    }
    // Ignore trivial nudges — a value change of < 1 cent doesn't earn
    // a tween.
    if (Math.abs(target - value) < 0.005) {
      setValue(target);
      return;
    }
    const controls = animate(value, target, {
      duration,
      ease: "easeOut",
      onUpdate: (v) => setValue(Math.round(v * 100) / 100),
    });
    return () => controls.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration]);

  return value;
}
